import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { AuthActivationRoute } from '../auth/auth-activation.js';
import { passwordPolicyError } from '../auth/auth-activation.js';
import { BrandMark } from './BrandMark.js';

interface AuthActivationScreenProps {
  route: AuthActivationRoute;
  onVerify(tokenHash: string, type: AuthActivationRoute['expectedType']): Promise<boolean>;
  onUpdatePassword(password: string): Promise<boolean>;
  onComplete(): void;
  onCancel(): void;
}

type ActivationPhase = 'VERIFYING' | 'READY' | 'SUBMITTING' | 'SUCCESS' | 'INVALID';

export function AuthActivationScreen({
  route,
  onVerify,
  onUpdatePassword,
  onComplete,
  onCancel,
}: AuthActivationScreenProps) {
  const [phase, setPhase] = useState<ActivationPhase>(route.tokenHash ? 'VERIFYING' : 'INVALID');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [message, setMessage] = useState<string>();
  const verificationRef = useRef<Promise<boolean> | null>(null);
  const submittingRef = useRef(false);

  useEffect(() => {
    const tokenHash = route.tokenHash;
    if (!tokenHash) return;
    verificationRef.current ??= Promise.resolve().then(() =>
      onVerify(tokenHash, route.expectedType)
    );
    let active = true;
    void verificationRef.current
      .then((verified) => {
        if (active) setPhase(verified ? 'READY' : 'INVALID');
      })
      .catch(() => {
        if (active) setPhase('INVALID');
      });
    return () => {
      active = false;
    };
  }, [onVerify, route.expectedType, route.tokenHash]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (phase !== 'READY' || submittingRef.current) return;
    const policyMessage = passwordPolicyError(password);
    if (policyMessage) {
      setMessage(policyMessage);
      return;
    }
    if (password !== confirmation) {
      setMessage('The passwords do not match.');
      return;
    }

    setMessage(undefined);
    submittingRef.current = true;
    setPhase('SUBMITTING');
    let updated: boolean;
    try {
      updated = await onUpdatePassword(password);
    } catch {
      updated = false;
    } finally {
      submittingRef.current = false;
    }
    if (updated) {
      setPhase('SUCCESS');
    } else {
      setMessage('The password could not be updated. Check the requirements and try again.');
      setPhase('READY');
    }
  };

  const isInvite = route.expectedType === 'invite';
  const title = isInvite ? 'Activate your workspace access' : 'Choose a new password';

  return (
    <main className="system-state auth-activation" aria-labelledby="activation-title">
      <div className="system-state__panel auth-activation__panel">
        <BrandMark />
        <p className="eyebrow">{isInvite ? 'Invitation acceptance' : 'Password recovery'}</p>
        <h1 id="activation-title">{title}</h1>

        {phase === 'VERIFYING' && (
          <>
            <p>Verifying this secure link before allowing a password change.</p>
            <span className="loading-line" aria-hidden="true" />
          </>
        )}

        {phase === 'INVALID' && (
          <>
            <div className="notice notice--error" role="alert">
              This link is invalid, expired, or has already been used. Request a new link and try again.
            </div>
            <button className="secondary-action auth-activation__return" type="button" onClick={onCancel}>
              Return to sign in
            </button>
          </>
        )}

        {(phase === 'READY' || phase === 'SUBMITTING') && (
          <form onSubmit={(event) => void submit(event)}>
            <p className="form-intro">Use at least 12 characters with lowercase, uppercase, a number, and a symbol.</p>
            {message && <div className="notice notice--error" role="alert">{message}</div>}
            <label htmlFor="activation-password">New password</label>
            <input id="activation-password" name="new-password" type="password" autoComplete="new-password" required disabled={phase === 'SUBMITTING'} value={password} onChange={(event) => setPassword(event.target.value)} />
            <label htmlFor="activation-confirmation">Confirm new password</label>
            <input id="activation-confirmation" name="confirm-password" type="password" autoComplete="new-password" required disabled={phase === 'SUBMITTING'} value={confirmation} onChange={(event) => setConfirmation(event.target.value)} />
            <button className="primary-action" type="submit" disabled={phase === 'SUBMITTING'}>
              {phase === 'SUBMITTING' ? 'Saving password…' : 'Save password'}
            </button>
          </form>
        )}

        {phase === 'SUCCESS' && (
          <>
            <div className="notice" role="status">Your password was updated successfully.</div>
            <button className="primary-action" type="button" onClick={onComplete}>Continue to workspace</button>
          </>
        )}
      </div>
    </main>
  );
}
